import React, { createContext, useContext, useState, useEffect } from 'react';
import translations from '../locales/translations';

// Average conversion rate: 1 INR = 0.055 MYR (approximately)
const CONVERSION_RATE = 0.04275;

const countries = {
  IN: {
    code: 'IN',
    name: 'India',
    currency: 'INR',
    symbol: '₹',
    flag: '🇮🇳',
    conversionRate: 1, // Base currency
    defaultLanguage: 'en',
  },
  MY: {
    code: 'MY',
    name: 'Malaysia',
    currency: 'MYR',
    symbol: 'RM',
    flag: '🇲🇾',
    conversionRate: CONVERSION_RATE, // 1 INR = 0.055 MYR
    defaultLanguage: 'bm',
  },
};

const languages = {
  en: {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇬🇧',
  },
  bm: {
    code: 'bm',
    name: 'Bahasa Malaysia',
    nativeName: 'Bahasa Melayu',
    flag: '🇲🇾',
  },
};

const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
  const getInitialCountryAndLanguage = () => {
    const path = window.location.pathname.split('/')[1];

    if (path === 'my') {
      return { country: countries.MY, lang: languages.bm };
    } else if (path === 'id') {
      // Provide fallback for other countries if needed
      return { country: countries.IN, lang: languages.en };
    } else if (path === 'th') {
      return { country: countries.IN, lang: languages.en };
    }

    return { country: countries.IN, lang: languages.en };
  };

  const initialSettings = getInitialCountryAndLanguage();

  const [selectedCountry, setSelectedCountry] = useState(initialSettings.country);
  const [selectedLanguage, setSelectedLanguage] = useState(initialSettings.lang);
  const [languageManuallySet, setLanguageManuallySet] = useState(false);

  // Auto-switch language when country changes (unless manually set)
  useEffect(() => {
    if (!languageManuallySet) {
      const defaultLang = selectedCountry.defaultLanguage;
      setSelectedLanguage(languages[defaultLang]);
    }
  }, [selectedCountry, languageManuallySet]);

  const convertPrice = (priceInINR) => {
    const converted = priceInINR * selectedCountry.conversionRate;
    // Round to appropriate decimal places
    if (selectedCountry.code === 'MY' && priceInINR == 1300) {
      return 400;
    }
    if (selectedCountry.code === 'MY') {
      return Math.round(converted * 100) / 100; // 2 decimal places for MYR
    }

    return Math.round(converted); // No decimals for INR
  };

  const formatPrice = (priceInINR, showSymbol = true) => {
    const converted = convertPrice(priceInINR);
    const formatted = converted.toLocaleString(
      selectedCountry.code === 'IN' ? 'en-IN' : 'en-MY',
      {
        minimumFractionDigits: selectedCountry.code === 'MY' ? 2 : 0,
        maximumFractionDigits: selectedCountry.code === 'MY' ? 2 : 0,
      },
    );
    return showSymbol ? `${selectedCountry.symbol}${formatted}` : formatted;
  };

  const changeCountry = (countryCode) => {
    if (countries[countryCode]) {
      setSelectedCountry(countries[countryCode]);
      // Reset manual language flag when country changes
      setLanguageManuallySet(false);
    }
  };

  const changeLanguage = (langCode) => {
    if (languages[langCode]) {
      setSelectedLanguage(languages[langCode]);
      setLanguageManuallySet(true); // Mark as manually set
    }
  };

  // Translation function
  const t = (key, replacements = {}) => {
    const keys = key.split('.');
    let value = translations[selectedLanguage.code];

    for (const k of keys) {
      if (value && value[k] !== undefined) {
        value = value[k];
      } else {
        // Fallback to English if key not found
        value = translations.en;
        for (const fallbackKey of keys) {
          if (value && value[fallbackKey] !== undefined) {
            value = value[fallbackKey];
          } else {
            return key; // Return key if not found
          }
        }
        break;
      }
    }

    // Handle replacements like {amount}, {count}
    if (typeof value === 'string') {
      Object.keys(replacements).forEach((placeholder) => {
        value = value.replace(
          new RegExp(`\\{${placeholder}\\}`, 'g'),
          replacements[placeholder],
        );
      });
    }

    return value;
  };

  // Get array of translations (for FAQs, etc.)
  const tArray = (key) => {
    const keys = key.split('.');
    let value = translations[selectedLanguage.code];

    for (const k of keys) {
      if (value && value[k] !== undefined) {
        value = value[k];
      } else {
        // Fallback to English
        value = translations.en;
        for (const fallbackKey of keys) {
          if (value && value[fallbackKey] !== undefined) {
            value = value[fallbackKey];
          } else {
            return [];
          }
        }
        break;
      }
    }

    return Array.isArray(value) ? value : [];
  };

  return (
    <CurrencyContext.Provider
      value={{
        // Currency
        selectedCountry,
        countries,
        changeCountry,
        convertPrice,
        formatPrice,
        symbol: selectedCountry.symbol,

        // Language
        selectedLanguage,
        languages,
        changeLanguage,
        t,
        tArray,
        isEnglish: selectedLanguage.code === 'en',
        isBahasaMalaysia: selectedLanguage.code === 'bm',
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
